import { useState } from 'react';
import { useAppStore } from '@/store';
import { toLUTICC, toLUTJSON, toLUTCSV } from '@/engine/export';

function downloadBlob(data: Uint8Array | string, filename: string, mime: string) {
  const blob = data instanceof Uint8Array
    ? new Blob([data.buffer as ArrayBuffer], { type: mime })
    : new Blob([data], { type: mime });
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function DownloadButton({
  label, sublabel, onClick,
}: { label: string; sublabel: string; onClick: () => void }) {
  return (
    <button onClick={onClick}
      className="flex items-center gap-3 w-full px-4 py-3 rounded-lg border border-slate-700
                 hover:border-sky-600 hover:bg-sky-900/20 text-left transition-colors group">
      <span className="text-sky-400 text-lg group-hover:scale-110 transition-transform">↓</span>
      <div>
        <p className="text-sm font-medium text-slate-200">{label}</p>
        <p className="text-xs text-slate-500">{sublabel}</p>
      </div>
    </button>
  );
}

function MetaField({ label, value, onChange }: {
  label: string; value: string; onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <label className="w-28 text-xs text-slate-400 shrink-0">{label}</label>
      <input value={value} onChange={e => onChange(e.target.value)}
        className="flex-1 bg-slate-800 border border-slate-700 rounded px-3 py-1.5
                   text-sm text-slate-200 font-mono focus:outline-none focus:border-sky-500" />
    </div>
  );
}

function ValidationSummary({ validation }: { validation: import('@/types').LUTValidation }) {
  const ok = (flag: boolean, yes: string, no: string) => (
    <span className={flag ? 'text-emerald-400' : 'text-red-400'}>
      {flag ? '✓' : '✕'} {flag ? yes : no}
    </span>
  );
  return (
    <div className="flex flex-wrap gap-4 text-sm font-mono">
      {ok(validation.monotonic, 'Monotonic', 'Non-monotonic')}
      {ok(validation.maxError < 1.0, `Max ΔE ${validation.maxError.toFixed(2)}`, `Max ΔE ${validation.maxError.toFixed(2)}`)}
      {ok(validation.meanError < 0.5, `Mean ΔE ${validation.meanError.toFixed(2)}`, `Mean ΔE ${validation.meanError.toFixed(2)}`)}
    </div>
  );
}

// =============================================================================
// ExportView
// =============================================================================

export default function ExportView() {
  const lut     = useAppStore(s => s.session.lut);
  const patches = useAppStore(s => s.session.patches);
  const setView = useAppStore(s => s.setView);

  const [desc,  setDesc]  = useState('RGB Pre-Cal DLP');
  const [model, setModel] = useState('');
  const [media, setMedia] = useState('');
  const [mode,  setMode]  = useState('');

  if (!lut) {
    return (
      <div className="flex items-center justify-center h-full text-slate-500 font-mono text-sm">
        Build a LUT first.
      </div>
    );
  }

  const meta = { description: desc || 'RGB Calibration LUT' };

  const handleICC  = () => downloadBlob(toLUTICC(lut, meta), 'rgb-calibration.icm', 'application/vnd.iccprofile');
  const handleJSON = () => downloadBlob(toLUTJSON(lut, { ...meta, patches: patches ?? undefined }), 'rgb-calibration.json', 'application/json');
  const handleCSV  = () => downloadBlob(toLUTCSV(lut), 'rgb-calibration-lut.csv', 'text/csv');

  return (
    <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-slate-100">Export</h1>
        <p className="text-xs text-slate-400 mt-0.5">
          {lut.gridSize}³ grid · {lut.options.interpolation} · built in {lut.buildTimeMs.toFixed(0)} ms
        </p>
      </div>

      {/* Metadata */}
      <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4 space-y-3">
        <p className="text-xs text-slate-400 uppercase tracking-wide">Profile metadata</p>
        <MetaField label="Description" value={desc}  onChange={setDesc}  />
        <MetaField label="Device model" value={model} onChange={setModel} />
        <MetaField label="Media type"  value={media} onChange={setMedia} />
        <MetaField label="Print mode"  value={mode}  onChange={setMode}  />
      </div>

      {/* Validation */}
      {lut.validation && (
        <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4 space-y-2">
          <p className="text-xs text-slate-400 uppercase tracking-wide">Validation</p>
          <ValidationSummary validation={lut.validation} />
        </div>
      )}

      {/* Downloads */}
      <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4 space-y-3">
        <p className="text-xs text-slate-400 uppercase tracking-wide">Downloads</p>
        <DownloadButton
          label="DeviceLink ICC (.icm)"
          sublabel="Primary output — import into profiling software"
          onClick={handleICC}
        />
        <DownloadButton
          label="Session data (.json)"
          sublabel="Includes LUT + measurements — reload later"
          onClick={handleJSON}
        />
        <DownloadButton
          label="LUT grid (.csv)"
          sublabel="Tabular input→output values for all grid nodes"
          onClick={handleCSV}
        />
      </div>

      {/* Navigate to Apply step */}
      <button onClick={() => setView('apply')}
        className="w-full py-2.5 rounded-xl border border-emerald-600
                   text-emerald-400 text-sm font-medium hover:bg-emerald-900/30 transition-colors">
        → Apply DeviceLink to image or ICC profile
      </button>

      {/* Next steps */}
      <div className="rounded-xl border border-slate-700 bg-slate-800/40 p-4 space-y-2">
        <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Next steps</p>
        {[
          'Apply this DeviceLink ICC in your profiling software (before the characterization target)',
          'Print your characterization target through the DeviceLink',
          'Measure and build the ICC output profile',
          'The profile now describes a predictable, linearized device',
        ].map((s, i) => (
          <p key={i} className="text-sm text-slate-300">
            <span className="font-mono text-sky-500 mr-2">{i + 1}.</span>{s}
          </p>
        ))}
      </div>
    </div>
  );
}
